import { Check, ChevronDown } from "lucide-react";
import { Select as RSelect } from "radix-ui";
import { cn } from "../lib/cn";
import { useUiText } from "../lib/locale";
import type { Option } from "../lib/option";
import { controlSize, fieldShell, type ControlSize } from "../lib/variants";
import { useField } from "./field";

export type SelectProps<V extends string = string> = {
  value: V | null;
  onValueChange: (value: V) => void;
  options: Option<V>[];
  placeholder?: string;
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Подпись для экранных читалок, когда списка нет внутри Field */
  "aria-label"?: string;
};

/** Короткий список без поиска (≤ ~10 вариантов). Для длинных/удалённых списков — SearchSelect. */
export function Select<V extends string = string>({
  value, onValueChange, options, placeholder, size = "md", invalid, disabled, className, id, name, "aria-label": ariaLabel,
}: SelectProps<V>) {
  const field = useField();
  const text = useUiText();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled;
  return (
    // Radix вызывает onValueChange(""), когда значения нет среди опций — игнорируем
    <RSelect.Root value={value ?? ""} onValueChange={(v) => v && onValueChange(v as V)} disabled={isDisabled} name={name}>
      <RSelect.Trigger
        id={id ?? field?.id}
        aria-label={ariaLabel}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        data-invalid={isInvalid}
        data-disabled={!!isDisabled}
        className={cn(fieldShell, controlSize({ size }), "justify-between text-start outline-none focus-visible:border-primary data-[placeholder]:text-placeholder", className)}
      >
        <span className="min-w-0 truncate"><RSelect.Value placeholder={placeholder ?? text.select} /></span>
        <RSelect.Icon className="shrink-0"><ChevronDown className="size-4 text-muted" /></RSelect.Icon>
      </RSelect.Trigger>
      <RSelect.Portal>
        <RSelect.Content
          position="popper"
          sideOffset={6}
          className="z-[60] max-h-[min(var(--radix-select-content-available-height),360px)] min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-1rem)] overflow-hidden rounded-tile border border-border bg-surface p-1 text-fg shadow-xl data-[state=open]:animate-in"
        >
          <RSelect.Viewport>
            {options.map((o) => (
              <RSelect.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex min-h-11 cursor-pointer items-center gap-2 rounded-lg py-2 ps-3 pe-9 text-sm outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-2  [&_svg]:size-4"
              >
                {o.icon}
                <div className="min-w-0 break-words">
                  <RSelect.ItemText>{o.label}</RSelect.ItemText>
                  {o.description && <div className="truncate text-xs text-muted">{o.description}</div>}
                </div>
                <RSelect.ItemIndicator className="absolute end-3"><Check className="text-primary" /></RSelect.ItemIndicator>
              </RSelect.Item>
            ))}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  );
}
